import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  AuthMethod,
  BillingStatus,
  CompanyType,
  Currency,
  DateFormat,
  Language,
  SaasBillingPlan,
  SystemOfMeasure,
  TimeFormat,
  TimeZone,
  UnitOfDistance,
  UsageStatus,
  WorkWeekStartDay,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { capitalize, get, startCase } from 'lodash-es';
import React, {
  ComponentProps,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { ControllerProps, FieldErrors, SubmitHandler, useForm } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import { RadioGroupFormField } from '~components/FormFields/RadioGroupFormField';
import { ReadOnlyField } from '~components/FormFields/ReadOnlyField';
import { TextFormField } from '~components/FormFields/TextFormField';
import AutocompleteField from '~components/FormFields/v2/Autocomplete/AutocompleteField';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { LngLat } from '~components/Maps/interfaces';
import { MarkerType } from '~components/Maps/v2/constants';
import { MapV2 } from '~components/Maps/v2/MapV2';
import { TreadMarkerProps } from '~components/Maps/v2/types';
import { AllowedAuthMethod, countries, data as enums } from '~constants/enums';
import { DEFAULT_MAP_CENTER } from '~constants/mapConsts';
import { FormStateChangeProps } from '~formsShared/types';
import { AdminCompany } from '~hooks/admin/useCompaniesAdmin/models';
import { addressIsGeocoded, AddressItem } from '~hooks/useAddress/models/AddressItem';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { Company, CompanyBasic } from '~hooks/useCompany/models';
import {
  CompanyContactFormValue,
  ContactItem,
} from '~hooks/useContact/models/ContactItem';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { makeCompanyCreateSchema } from '~pages/Admin/Companies/companyValidationSchema';
import { ImageUpload } from '~pages/Admin/Companies/ImageUpload';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { allowedTimezones } from '~utils/dateTime';

const authMethodsOptions: Record<AllowedAuthMethod, string> = {
  magic_link: t('auth_methods.magic_link'),
  otp_sms: t('auth_methods.otp_sms'),
  password: t('auth_methods.password'),
  sso: t('auth_methods.sso'),
};

const workWeekStartDayOptions: AutocompleteFieldOption<WorkWeekStartDay>[] =
  Object.values(WorkWeekStartDay).map((day) => ({
    label: t(`enums.work_week_start_day.${day}`),
    value: day,
    _entity: day,
  }));

const FIXED_AUTH_METHODS: AuthMethod[] = [AuthMethod.PASSWORD, AuthMethod.SSO];

// Splits the backend's single `contacts.name` column into the two inputs this form
// shows. The last whitespace-delimited token is the last name and everything before it
// is the first name, so multi-word first names ("Mary Jane Watson") round-trip through
// `ContactItem.deparseCompany`'s rejoin. A single-token name ("Cher") becomes the FIRST
// name: putting it in `lastName` instead left `firstName` empty, and with `firstName`
// required the form could never be submitted at all.
const extractNames = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);

  if (parts.length < 2) {
    return { firstName: parts[0] ?? '', lastName: '' };
  }

  const lastName = parts.pop() ?? '';

  return { firstName: parts.join(' '), lastName };
};

const formatAddress = (address: {
  streetAddress?: string;
  premise?: string;
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  country?: { name?: string; code?: string } | null;
}): string => {
  const parts = [
    address.streetAddress,
    address.premise,
    address.locality,
    address.administrativeArea,
    address.postalCode,
    address.country?.name,
  ].filter(Boolean);
  return parts.join(', ');
};

// The shape CompanyForm actually reads off `defaultCompany` for prefill purposes.
// Callers that don't have a full Company/AdminCompany instance (e.g. promoting an
// Account, which has no Company record yet) can build one of these directly instead
// of casting a partial object into a model class that requires private fields.
export interface CompanyFormPrefillData {
  parentCompany?: CompanyBasic | null;
  id?: string;
  legalName?: string;
  dbaName?: string;
  companyType?: CompanyType | null;
  address?: Pick<
    AddressItem,
    | 'streetAddress'
    | 'premise'
    | 'locality'
    | 'administrativeArea'
    | 'postalCode'
    | 'country'
    | 'lat'
    | 'lng'
    | 'placeId'
  > | null;
  primaryContact?: Pick<ContactItem, 'name' | 'email' | 'phone'> | null;
  billingContact?: Pick<ContactItem, 'name' | 'email' | 'phone'> | null;
  defaultLat?: number | null;
  defaultLon?: number | null;
  authMethods?: AuthMethod[];
  logoUrl?: string | null;
  // Company-wide settings this form renders. A prefill source has no Company record
  // yet, so all of these fall back to the enum defaults — but they must be DECLARED,
  // otherwise reading them off the `defaultCompany` union widens every one to `string`
  // and the payload type gate downstream stops meaning anything.
  billingStatus?: BillingStatus | null;
  currency?: Currency | null;
  dateFormat?: DateFormat | null;
  language?: Language | null;
  saasBillingPlan?: SaasBillingPlan | null;
  systemOfMeasure?: SystemOfMeasure | null;
  timeFormat?: TimeFormat | null;
  timeZone?: TimeZone | null;
  unitOfDistance?: UnitOfDistance | null;
  usageStatus?: UsageStatus | null;
  workWeekStartDay?: WorkWeekStartDay | null;
}

// The values `useForm` holds for this form. Declared explicitly rather than inferred
// from `getCompanyDefaultValues`: that function reads a three-way union through lodash
// `get`, which resolves to `any` for most keys, and an `any`-riddled form type assigns
// to anything a serializer asks for. That is precisely what let CUS-447 through.
export interface CompanyFormValues {
  address: {
    administrativeArea: string;
    country: { code: string; name: string } | undefined;
    lat: number | null;
    lng: number | null;
    locality: string;
    placeId: string;
    postalCode: string;
    premise: string;
    streetAddress: string;
  };
  authMethods: AuthMethod[];
  billingContact: Required<CompanyContactFormValue>;
  billingStatus: BillingStatus;
  companyType: CompanyType | null;
  currency: Currency;
  dateFormat: DateFormat;
  defaultAddress: AddressItem | null;
  defaultLat: number | null;
  defaultLon: number | null;
  doingBusinessName: string;
  externalStripeCustomerId: string;
  id: string;
  language: Language;
  legalName: string;
  parentCompany: CompanyBasic | undefined;
  primaryContact: Required<CompanyContactFormValue>;
  saasBillingPlan: SaasBillingPlan;
  systemOfMeasure: SystemOfMeasure;
  timeFormat: TimeFormat;
  timeZone: TimeZone;
  unitOfDistance: { label: string; value: UnitOfDistance };
  usageStatus: UsageStatus;
  workWeekStartDay: AutocompleteFieldOption<WorkWeekStartDay>;
}

interface CreateCompanyFormProps {
  defaultCompany: Company | AdminCompany | CompanyFormPrefillData | null | undefined;
  onFormStateChange: ({ isValid, isDirty }: FormStateChangeProps) => void;
  onChangeLogo: (logo: Nullable<File>) => void;
  // Fired when the user removes the logo; its meaning depends on the parent's
  // upload mode. Immediate-mode parents (GeneralSettings), whose onChangeLogo
  // already uploaded the chosen file, must delete the persisted logo
  // server-side — including on an undo of a file chosen moments ago. Staged
  // parents should instead treat it as "stage the existing logo's deletion for
  // Submit" (Admin > Companies will wire this in TRE-15939). Omit it when the
  // parent can't persist a deletion: removing the existing logo then leaves the
  // form clean so Submit doesn't promise a change that won't happen.
  onRemoveLogo?: () => void;
  editable?: boolean;
  companyOptions?: CompanyBasic[];
  isAdminPage?: boolean;
  // A server-side logo upload failure (e.g. a 422 content-type rejection), surfaced
  // inline on the Company Logo card by the caller once the upload settles.
  logoErrorMessage?: Nullable<string>;
  logoUrl?: string | null;
  // The account promotion flow requires a contact email unconditionally, unlike
  // the default company create/edit flows which accept email OR phone.
  requireContactEmail?: boolean;
}

const getCompanyDefaultValues = (
  defaultCompany?: Company | AdminCompany | CompanyFormPrefillData | null,
): CompanyFormValues => {
  const primaryNames = extractNames(defaultCompany?.primaryContact?.name ?? '');
  const billingNames = extractNames(defaultCompany?.billingContact?.name ?? '');
  const unitOfDistance = defaultCompany?.unitOfDistance ?? enums.unit_of_distance.default;
  const workWeekStartDay =
    defaultCompany?.workWeekStartDay ?? enums.work_week_start_day.default;

  return {
    parentCompany: defaultCompany?.parentCompany || undefined,
    id: defaultCompany?.id || '',
    legalName: defaultCompany?.legalName || '',
    doingBusinessName: defaultCompany?.dbaName || '',
    companyType: defaultCompany?.companyType ?? null,
    address: {
      streetAddress: defaultCompany?.address?.streetAddress ?? '',
      premise: defaultCompany?.address?.premise ?? '',
      locality: defaultCompany?.address?.locality ?? '',
      administrativeArea: defaultCompany?.address?.administrativeArea ?? '',
      postalCode: defaultCompany?.address?.postalCode ?? '',
      country: defaultCompany?.address?.country ?? countries.default,
      lat: defaultCompany?.address?.lat ?? null,
      lng: defaultCompany?.address?.lng ?? null,
      // Carried so a company that re-picks its address sends the NEW place_id.
      // Leaving it out of the form value meant the deparse always read `undefined`
      // and the stale place_id stayed on the record.
      placeId: defaultCompany?.address?.placeId ?? '',
    },
    defaultAddress: null,
    // NOT defaulted to DEFAULT_MAP_CENTER. `deparseUpdate` sends whatever sits here
    // on every save, so seeding the fallback stamped it onto any company that had no
    // coordinates — the moment someone edited an unrelated field. Companies saved
    // before TRE-14756 got Tread's Toronto office that way. The map still CENTRES on
    // the fallback for display (see the MapV2 `center` prop); it just is not a value
    // we write back.
    defaultLat: defaultCompany?.defaultLat ?? null,
    defaultLon: defaultCompany?.defaultLon ?? null,
    primaryContact: {
      firstName: primaryNames.firstName,
      lastName: primaryNames.lastName,
      email: defaultCompany?.primaryContact?.email ?? '',
      phone: defaultCompany?.primaryContact?.phone ?? '',
    },
    billingContact: {
      firstName: billingNames.firstName,
      lastName: billingNames.lastName,
      email: defaultCompany?.billingContact?.email ?? '',
      phone: defaultCompany?.billingContact?.phone ?? '',
    },
    usageStatus: defaultCompany?.usageStatus ?? enums.usage_status.default,
    billingStatus: defaultCompany?.billingStatus ?? enums.billing_status.default,
    saasBillingPlan: defaultCompany?.saasBillingPlan ?? enums.saas_billing_plan.default,
    // Only the admin read returns the Stripe customer id (and only to platform
    // admins), so AdminCompany is the one prefill source; every other source starts
    // blank. Clearing a prefilled value unlinks (AdminCompany.deparse sends null).
    // Unlike the sibling admin-only fields above, this can't join the uniform
    // `defaultCompany?.field` pattern: Company never declares the property at all
    // (not even as undefined), so reading it off the union needs a runtime check.
    externalStripeCustomerId:
      (defaultCompany instanceof AdminCompany
        ? defaultCompany.externalStripeCustomerId
        : null) ?? '',
    systemOfMeasure: defaultCompany?.systemOfMeasure ?? enums.system_of_measure.default,
    unitOfDistance: { value: unitOfDistance, label: capitalize(unitOfDistance) },
    language: defaultCompany?.language ?? enums.language.default,
    dateFormat: defaultCompany?.dateFormat ?? enums.date_format.default,
    timeFormat: defaultCompany?.timeFormat ?? enums.time_format.default,
    currency: defaultCompany?.currency ?? enums.currency.default,
    timeZone: defaultCompany?.timeZone ?? enums.time_zone.default,
    authMethods: (defaultCompany?.authMethods ?? []).filter(
      (method) => method in authMethodsOptions,
    ),
    workWeekStartDay: {
      label: t(`enums.work_week_start_day.${workWeekStartDay}`),
      value: workWeekStartDay,
      _entity: workWeekStartDay,
    },
  };
};

export interface CompanyFormHandler {
  submit: (callBack: SubmitHandler<CompanyFormValues>) => void;
  resetForm: (callBack?: () => void) => void;
}

const CompanyForm = forwardRef(function CompanyForm(
  {
    defaultCompany,
    onFormStateChange,
    onChangeLogo,
    onRemoveLogo,
    editable = true,
    companyOptions,
    isAdminPage = false,
    logoErrorMessage,
    logoUrl: logoUrlProp,
    requireContactEmail = false,
  }: CreateCompanyFormProps,
  ref: Ref<CompanyFormHandler>,
) {
  const { toasterStore } = useStores();
  const addressProvider: SupportedAddressProvider =
    SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH;
  const { getPlaces, encodeLngLat, getAddressItemByGoogleMapsPlaceId } = useGeocode({
    addressProvider,
  });

  const isEditing = Boolean(defaultCompany?.id);
  const resolvedLogoUrl =
    logoUrlProp !== undefined ? logoUrlProp : defaultCompany?.logoUrl;
  const defaultValues = getCompanyDefaultValues(defaultCompany);
  const schema = useMemo(
    () => makeCompanyCreateSchema(requireContactEmail),
    [requireContactEmail],
  );
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<CompanyFormValues>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const [defaultAddress, setDefaultAddress] = useState<AddressItem | string>('');
  // ImageUpload stages the chosen file/pending deletion in its own local state
  // rather than through react-hook-form, so react-hook-form's `isDirty` never
  // reflects a logo-only change. Track it here and fold it into the dirty
  // state reported to the parent so Submit enables the same way it would for
  // any other field edit (TRE-15938). "added" = a new file is staged;
  // "removed" = the existing logo is marked for deletion; choosing then
  // removing a file is an undo back to "none".
  const [stagedLogoChange, setStagedLogoChange] = useState<'none' | 'added' | 'removed'>(
    'none',
  );
  // ImageUpload also keeps the staged file/preview in its own uncontrolled
  // state, which resetForm (Cancel) can't reach through props — and its ref
  // reset() clears to empty rather than back to the server logo. Remounting it
  // with a fresh key rebuilds that state from the `url` prop, restoring the
  // server-side logo in every case (staged file cleared, staged removal undone).
  const [imageUploadKey, setImageUploadKey] = useState(0);

  useEffect(() => {
    if (defaultCompany) {
      reset(getCompanyDefaultValues(defaultCompany));
      setStagedLogoChange('none');
    }
  }, [defaultCompany, reset]);

  useEffect(() => {
    onFormStateChange({ isDirty: isDirty || stagedLogoChange !== 'none', isValid });
  }, [isValid, isDirty, stagedLogoChange]);

  // Sets the initial marker
  useEffect(() => {
    handleCoordinateChange(getValues('defaultLat'), getValues('defaultLon'));
  }, []);

  // Without this, a failed validation is completely silent: `handleSubmit` swallows it,
  // the inline errors land on cards far below a sticky Update button, and the button
  // reads as broken. Every consumer submits through the ref, so one toast here covers
  // Settings > General, Admin > Companies, and Promote Account.
  const notifyValidationFailed = () => {
    toasterStore.push(
      alert(t('form_validation_errors.form_has_errors'), AlertTypes.error),
    );
  };

  useImperativeHandle(
    ref,
    () => ({
      submit(callBack: SubmitHandler<CompanyFormValues>) {
        handleSubmit(callBack, notifyValidationFailed)();
      },
      resetForm(callBack?: () => void) {
        reset();
        setStagedLogoChange('none');
        setImageUploadKey((key) => key + 1);
        handleEncodeLatLon(getValues('defaultLat'), getValues('defaultLon'));
        handleCoordinateChange(getValues('defaultLat'), getValues('defaultLon'));
        callBack?.();
      },
    }),
    [],
  );

  const handleSelectAddress = async (item: AddressItem) => {
    let addressItem = item;

    if (addressProvider === SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH) {
      addressItem = await getAddressItemByGoogleMapsPlaceId(
        item.placeId,
        item.fullAddress ?? undefined,
      );
    }

    if (isNaN(addressItem.lat) || isNaN(addressItem.lng)) {
      return getValues('address.streetAddress');
    }

    // Deliberately does NOT touch the Default Map Centre card. `handleAddressChange`
    // resolves the place and writes defaultLat/defaultLon, so routing through it here
    // is what let a company-address edit overwrite a pin someone positioned on
    // purpose. The centre only FALLS BACK to the address while it has never been set
    // (see resolvedMapCenter, which watches address.lat/lng directly).
    setValue('address.streetAddress', addressItem.streetAddress.split(',')[0], {
      shouldDirty: true,
    });
    setValue('address.premise', addressItem.premise);
    setValue('address.locality', addressItem.locality);
    setValue('address.administrativeArea', addressItem.administrativeArea);
    setValue('address.postalCode', addressItem.postalCode);
    setValue('address.lat', addressItem.lat);
    setValue('address.lng', addressItem.lng);
    setValue('address.placeId', addressItem.placeId);

    const country =
      countries.values.find((c) => c.code === addressItem.country?.code) ??
      countries.default;

    setValue('address.country', country);

    return addressItem.streetAddress.split(',')[0];
  };

  // Sets the initial marker
  useEffect(() => {
    handleCoordinateChange(getValues('defaultLat'), getValues('defaultLon'));
  }, []);

  // Sets a new market according to the new coordinates, and updates the form values accordingly
  // This is also called when the user drags the marker
  const handleCoordinateChange = (lat: number | null, lon: number | null) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    setValue('defaultLat', lat);
    setValue('defaultLon', lon);
  };

  // Parse the input value to get the lat and lon if the user types them manually
  // Enables the user to input coordinates for their map center and address simultaneously
  const handleInputChange = async (
    e: React.SyntheticEvent<Element, Event> | null,
    value: string,
  ) => {
    const [latStr, lonStr] = value.split(',').map((coord: string) => coord.trim());
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);

    if (isNaN(lat) || isNaN(lon)) return;

    try {
      handleCoordinateChange(lat, lon);
      const newAddress = await encodeLngLat(`${lat},${lon}`);
      setDefaultAddress(newAddress);
      handleSelectAddress(newAddress);
    } catch (error) {
      console.error('Error encoding coordinates:', error);
    }
  };

  const handleEncodeLatLon = async (lat: number | null, lon: number | null) => {
    // Clear rather than return: `resetForm` (Cancel) relies on this to refresh the
    // Default Map Centre readout, so bailing out left the old geocoded string on
    // screen next to coordinates that had just been restored to null.
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      setDefaultAddress('');

      return;
    }

    const newAddress = await encodeLngLat(`${lat},${lon}`);
    setDefaultAddress(newAddress);
  };

  const handleAddressChange = (address: AddressItem | string) => {
    setDefaultAddress(address);

    if (typeof address !== 'string') {
      if (addressProvider === SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH) {
        getAddressItemByGoogleMapsPlaceId(
          address.placeId,
          address.fullAddress ?? undefined,
        ).then((addressItem) => {
          handleCoordinateChange(addressItem.lat, addressItem.lng);
        });
      } else {
        handleCoordinateChange(address.lat, address.lng);
      }
    }

    return address;
  };

  const handleLatLonChange = async (latLon: string) => {
    const [latStr, lonStr] = latLon.split(',').map((coord: string) => coord.trim());
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (!isNaN(lat) && !isNaN(lon)) {
      const newAddress = await encodeLngLat(`${lat},${lon}`);
      setDefaultAddress(newAddress);
      handleCoordinateChange(lat, lon);
    }
  };

  // An explicitly-set centre wins; otherwise fall back to the company's street address
  // so a company that never set one still opens somewhere useful, then to the global
  // constant. Display only — `getCompanyDefaultValues` keeps the form value null so the
  // fallback is never written back. Persisting it is what stamped Tread's Toronto
  // office onto every company that had no centre.
  const watchedDefaultLat = watch('defaultLat');
  const watchedDefaultLon = watch('defaultLon');
  const watchedAddressLat = watch('address.lat');
  const watchedAddressLng = watch('address.lng');

  // `Company.parse` uses null for an unset centre, so a 0 there is a real value and is
  // kept; the address side goes through the shared geocoded check.
  const hasGeocodedAddress = addressIsGeocoded(watchedAddressLat, watchedAddressLng);
  const centerLat = watchedDefaultLat ?? (hasGeocodedAddress ? watchedAddressLat : null);
  const centerLon = watchedDefaultLon ?? (hasGeocodedAddress ? watchedAddressLng : null);

  // Gated on whether a centre is actually KNOWN — not on the resolved value, which
  // always has the constant behind it and so would report "known" forever, putting a
  // draggable marker on Kansas that a save could turn into the company's real centre.
  const hasResolvedMapCenter = centerLat !== null && centerLon !== null;
  const resolvedMapCenter = {
    lat: centerLat ?? DEFAULT_MAP_CENTER.lat,
    lng: centerLon ?? DEFAULT_MAP_CENTER.lng,
  };

  // Keeps the Default Map Centre address readout in step with whatever coordinates the
  // card is showing. It used to run once on mount against the RAW form value, so a
  // company whose centre derives from its street address displayed coordinates beside
  // a blank address. Resolved values only change when a coordinate does, so this does
  // not geocode on every keystroke.
  useEffect(() => {
    handleEncodeLatLon(
      hasResolvedMapCenter ? resolvedMapCenter.lat : null,
      hasResolvedMapCenter ? resolvedMapCenter.lng : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResolvedMapCenter, resolvedMapCenter.lat, resolvedMapCenter.lng]);

  const markersV2 = useMemo(
    () =>
      (hasResolvedMapCenter ? [resolvedMapCenter] : []).map((loc) => ({
        id: 'default-site-location',
        type: MarkerType.site,
        lat: loc.lat,
        lng: loc.lng,
        draggable: editable,
        onDragEnd: editable
          ? (e: LngLat) => {
              handleLatLonChange(`${e.lat},${e.lng}`); // Code smell but we use for parity
              setValue('defaultLat', e.lat, { shouldDirty: true });
              setValue('defaultLon', e.lng, { shouldDirty: true });
              handleCoordinateChange(e.lat, e.lng); // Code smell but we use for parity
            }
          : undefined,
      })) as Omit<TreadMarkerProps, 'onMouseUp' | 'onMouseDown'>[] as ComponentProps<
        typeof MapV2
      >['markers'],
    [
      resolvedMapCenter.lat,
      resolvedMapCenter.lng,
      hasResolvedMapCenter,
      editable,
      handleLatLonChange,
      setValue,
      handleCoordinateChange,
    ],
  );

  const handleChangeLogo = (file: File) => {
    setStagedLogoChange('added');
    onChangeLogo(file);
  };

  // ImageUpload's delete button fires for both "undo the file I just chose" and
  // "remove the existing logo" — only this component knows which, via stagedLogoChange.
  const handleRemoveLogo = () => {
    if (stagedLogoChange === 'added') {
      // Undo: revert to the server state and clear the parent's staged copy so a
      // later Submit can't upload a logo the UI no longer shows. Immediate-mode
      // parents (GeneralSettings) still get onRemoveLogo, since their upload
      // already committed and the removal must delete it server-side.
      setStagedLogoChange('none');
      onChangeLogo(null);
      onRemoveLogo?.();
      return;
    }
    // Removing the existing logo only marks the form dirty when the parent can
    // actually persist the deletion; Admin > Companies doesn't wire onRemoveLogo
    // yet (TRE-15939), so Submit must not promise a change that won't happen.
    if (onRemoveLogo) {
      setStagedLogoChange('removed');
      onRemoveLogo();
    }
  };

  return (
    <Box component="form" data-test-id="company-form" sx={{ mt: isAdminPage ? 0 : 2 }}>
      {companyOptions && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: isAdminPage ? 0 : 2,
          }}
        >
          <AutocompleteFormField
            control={control}
            name="parentCompany"
            errors={errors}
            list={companyOptions}
            label={t('form_fields.parent_company')}
            isRequired={false}
            getValue={(item) => item.id}
            getLabel={(item) => item.legalName || ''}
          />
        </Box>
      )}
      <Card
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          mt: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant={'h6'} sx={{ mb: 2 }}>
          {t('company.form.account_information')}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                isRequired
                label={t('form_fields.legal_name')}
                name="legalName"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.legal_name')}
                value={watch('legalName')}
              />
            )}
          </Grid>

          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                name={'doingBusinessName'}
                label={t('form_fields.company_name')}
                isRequired={false}
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.company_name')}
                value={watch('doingBusinessName')}
              />
            )}
          </Grid>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <AutocompleteFormField
                control={control}
                errors={errors}
                getLabel={(item) => startCase(item)}
                getValue={(item) => item}
                isRequired
                label={t('form_fields.company_type')}
                list={enums.company_type.values}
                name="companyType"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.company_type')}
                value={startCase(watch('companyType') || '')}
              />
            )}
          </Grid>

          {editable ? (
            <>
              <Grid size={{ xs: 12 }}>
                <AutocompleteAsyncFormField
                  asyncCallback={getPlaces}
                  control={control}
                  debounceTime={500}
                  errors={errors}
                  getLabel={(item) => get(item, 'nameWithAddress', '')}
                  getValue={(item) => get(item, 'placeId', '')}
                  isRequired
                  label={t('form_fields.address')}
                  name="address.streetAddress"
                  onInput={(e, val) => {
                    handleInputChange(e, val);
                  }}
                  onSelect={handleSelectAddress}
                  placeholder={t('common.address_autocomplete_hint')}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  name="address.premise"
                  label={t('form_fields.address_2')}
                  isRequired={false}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  isRequired
                  label={t('form_fields.city')}
                  name="address.locality"
                />
              </Grid>

              <Grid size={{ xs: 6 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  name="address.administrativeArea"
                  label={t('form_fields.state')}
                  isRequired={false}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextFormField
                  control={control}
                  errors={errors}
                  isRequired
                  label={t('form_fields.zip')}
                  name="address.postalCode"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <AutocompleteFormField
                  control={control}
                  errors={errors}
                  getLabel={(item) => item.name}
                  getValue={(item) => item.code}
                  isRequired
                  label={t('form_fields.country')}
                  list={countries.values}
                  name="address.country"
                />
              </Grid>
            </>
          ) : (
            <Grid size={{ xs: 12 }}>
              <ReadOnlyField
                label={t('form_fields.address')}
                value={formatAddress(watch('address'))}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            {editable ? (
              <AutocompleteFormField
                clearable={false}
                control={control}
                errors={errors}
                getLabel={(item) => item}
                getValue={(item) => item}
                isRequired
                label={t('form_fields.time_zone')}
                list={allowedTimezones}
                name="timeZone"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.time_zone')}
                value={watch('timeZone')}
              />
            )}
          </Grid>
        </Grid>
      </Card>
      <Card
        elevation={0}
        sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant={'h6'} sx={{ mb: 2 }}>
          {t('navigation.default_map_center')}
        </Typography>

        <Grid container spacing={2}>
          {editable ? (
            <>
              <Grid size={{ xs: 6 }}>
                <AutocompleteAsyncFormField
                  asyncCallback={getPlaces}
                  control={control}
                  errors={errors}
                  getLabel={(item) => item.nameWithAddress}
                  getValue={(item) => item}
                  inputProps={{
                    value:
                      typeof defaultAddress === 'string'
                        ? defaultAddress
                        : defaultAddress?.streetAddress || '',
                    onInput: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleAddressChange(e.currentTarget.value),
                  }}
                  isRequired
                  label={t('form_fields.address')}
                  name="defaultAddress"
                  onSelect={(e: AddressItem) => handleAddressChange(e)}
                  placeholder={t('common.address_autocomplete_hint')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextFormField
                  control={control}
                  name="defaultLatLon"
                  errors={errors}
                  label={t('form_fields.coordinates')}
                  inputProps={{
                    value: hasResolvedMapCenter
                      ? `${resolvedMapCenter.lat}, ${resolvedMapCenter.lng}`
                      : '',
                    onInput: (e) => {
                      handleLatLonChange(e.currentTarget.value);
                      const [latStr, lonStr] = e.currentTarget.value
                        .split(',')
                        .map((coord: string) => coord.trim());
                      const lat = parseFloat(latStr);
                      const lon = parseFloat(lonStr);
                      if (!isNaN(lat) && !isNaN(lon)) {
                        setValue('defaultLat', lat, { shouldDirty: true });
                        setValue('defaultLon', lon, { shouldDirty: true });
                      }
                    },
                  }}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ xs: 6 }}>
                <ReadOnlyField
                  label={t('form_fields.address')}
                  value={
                    typeof defaultAddress === 'string'
                      ? defaultAddress
                      : defaultAddress?.streetAddress || ''
                  }
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <ReadOnlyField
                  label={t('form_fields.coordinates')}
                  value={
                    hasResolvedMapCenter
                      ? `${resolvedMapCenter.lat}, ${resolvedMapCenter.lng}`
                      : ''
                  }
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ height: '300px' }}>
              {/* 12 keeps the whole metro area in view so users can confirm the pin is in the right place */}
              <MapV2
                center={resolvedMapCenter}
                markers={markersV2}
                maxZoom={12}
                emptyText={t('navigation.choose_address')}
              />
            </Box>
          </Grid>
        </Grid>
      </Card>
      <Card
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          // MUI Card defaults to overflow: hidden, which clips the phone country dropdown.
          overflow: 'visible',
        }}
      >
        <Typography variant={'h6'} sx={{ mb: 2 }}>
          {t('company.form.contact_information')}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                isRequired
                label={t('form_fields.first_name')}
                name="primaryContact.firstName"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.first_name')}
                value={watch('primaryContact.firstName')}
              />
            )}
          </Grid>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                isRequired
                label={t('form_fields.last_name')}
                name="primaryContact.lastName"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.last_name')}
                value={watch('primaryContact.lastName')}
              />
            )}
          </Grid>

          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                name="primaryContact.email"
                label={t('form_fields.email')}
                isRequired={requireContactEmail}
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.email')}
                value={watch('primaryContact.email')}
              />
            )}
          </Grid>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <PhoneCodeFlagInput
                control={control as unknown as ControllerProps['control']}
                errors={errors as unknown as FieldErrors}
                name="primaryContact.phone"
                label={t('form_fields.phone')}
                isRequired={false}
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.phone')}
                value={watch('primaryContact.phone')}
              />
            )}
          </Grid>
        </Grid>
      </Card>
      <Card
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          // MUI Card defaults to overflow: hidden, which clips the phone country dropdown.
          overflow: 'visible',
        }}
      >
        <Typography variant={'h6'} sx={{ mb: 2 }}>
          {t('company.form.billing_contact')}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                isRequired
                label={t('form_fields.first_name')}
                name="billingContact.firstName"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.first_name')}
                value={watch('billingContact.firstName')}
              />
            )}
          </Grid>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                isRequired
                label={t('form_fields.last_name')}
                name="billingContact.lastName"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.last_name')}
                value={watch('billingContact.lastName')}
              />
            )}
          </Grid>

          <Grid size={{ xs: 6 }}>
            {editable ? (
              <TextFormField
                control={control}
                errors={errors}
                name="billingContact.email"
                label={t('form_fields.email')}
                isRequired={requireContactEmail}
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.email')}
                value={watch('billingContact.email')}
              />
            )}
          </Grid>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <PhoneCodeFlagInput
                control={control as unknown as ControllerProps['control']}
                errors={errors as unknown as FieldErrors}
                name="billingContact.phone"
                label={t('form_fields.phone')}
                isRequired={false}
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.phone')}
                value={watch('billingContact.phone')}
              />
            )}
          </Grid>
        </Grid>
      </Card>
      <Card
        elevation={0}
        sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant={'h6'} sx={{ mb: 2 }}>
          {t('company.form.company_defaults')}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <AutocompleteFormField
                clearable={false}
                control={control}
                errors={errors}
                getLabel={(item) => item.label}
                getValue={(item) => item.value}
                isRequired
                label={t('form_fields.unit_of_distance')}
                list={[...enums.unit_of_distance.values].map((value) => ({
                  label: value.charAt(0).toUpperCase() + value.slice(1),
                  value,
                }))}
                name="unitOfDistance"
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.unit_of_distance')}
                value={capitalize(watch('unitOfDistance')?.value || '')}
              />
            )}
          </Grid>
          <Grid size={{ xs: 6 }}>
            {editable ? (
              <AutocompleteField
                control={control}
                disableClearable
                label={t('form_fields.work_week_start_day')}
                name="workWeekStartDay"
                options={workWeekStartDayOptions}
              />
            ) : (
              <ReadOnlyField
                label={t('form_fields.work_week_start_day')}
                value={watch('workWeekStartDay')?.label || ''}
              />
            )}
          </Grid>
        </Grid>
      </Card>
      <Card
        elevation={0}
        sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant={'h6'} sx={{ mb: 2 }}>
          {t('company.form.company_logo')}
        </Typography>

        {editable ? (
          <ImageUpload
            key={imageUploadKey}
            url={resolvedLogoUrl}
            onChange={handleChangeLogo}
            removeCallback={handleRemoveLogo}
            errorMessage={logoErrorMessage}
            // Matches the API's Company#logo size validation (< 5MB)
            maxSize={5}
            sx={{ mt: 1, mb: 1 }}
          />
        ) : resolvedLogoUrl ? (
          <Box
            component="img"
            src={resolvedLogoUrl}
            alt={t('company.form.company_logo')}
            sx={{ maxHeight: 100, maxWidth: 300, objectFit: 'contain' }}
          />
        ) : (
          <ReadOnlyField label="" value="" />
        )}
      </Card>
      {isAdminPage && (
        <>
          <Card
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant={'h6'} sx={{ mb: 2 }}>
              {t('company.form.billing_settings')}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                {editable ? (
                  <AutocompleteFormField
                    clearable={false}
                    control={control}
                    errors={errors}
                    getLabel={(item) => capitalize(item)}
                    getValue={(item) => item}
                    isRequired
                    label={t('form_fields.usage_status')}
                    list={enums.usage_status.values}
                    name="usageStatus"
                  />
                ) : (
                  <ReadOnlyField
                    label={t('form_fields.usage_status')}
                    value={capitalize(watch('usageStatus') || '')}
                  />
                )}
              </Grid>

              <Grid size={{ xs: 4 }}>
                {editable ? (
                  <AutocompleteFormField
                    clearable={false}
                    control={control}
                    errors={errors}
                    getLabel={(item) => capitalize(item)}
                    getValue={(item) => item}
                    isRequired
                    label={t('form_fields.billing_status')}
                    list={enums.billing_status.values}
                    name="billingStatus"
                  />
                ) : (
                  <ReadOnlyField
                    label={t('form_fields.billing_status')}
                    value={capitalize(watch('billingStatus') || '')}
                  />
                )}
              </Grid>

              <Grid size={{ xs: 4 }}>
                {editable ? (
                  <AutocompleteFormField
                    clearable={false}
                    control={control}
                    errors={errors}
                    getLabel={(item) => capitalize(item)}
                    getValue={(item) => item}
                    isRequired
                    label={t('form_fields.saas_billing_plan')}
                    list={enums.saas_billing_plan.values}
                    name="saasBillingPlan"
                  />
                ) : (
                  <ReadOnlyField
                    label={t('form_fields.saas_billing_plan')}
                    value={capitalize(watch('saasBillingPlan') || '')}
                  />
                )}
              </Grid>

              {isEditing && (
                <Grid size={{ xs: 12 }}>
                  {editable ? (
                    <>
                      <TextFormField
                        control={control}
                        errors={errors}
                        isRequired={false}
                        label={t('form_fields.stripe_customer_id')}
                        name="externalStripeCustomerId"
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('company.form.stripe_customer_id_hint')}
                      </Typography>
                    </>
                  ) : (
                    <ReadOnlyField
                      label={t('form_fields.stripe_customer_id')}
                      value={watch('externalStripeCustomerId') || ''}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Card>

          {isEditing && (
            <Card
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant={'h6'} sx={{ mb: 2 }}>
                {t('company.form.authentication_settings')}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  {editable ? (
                    <AutocompleteFormField
                      control={control}
                      name="authMethods"
                      errors={errors}
                      list={Object.keys(authMethodsOptions).filter(
                        (method) =>
                          method !== AuthMethod.SSO ||
                          defaultCompany?.authMethods?.includes(AuthMethod.SSO),
                      )}
                      label={t('company.form.available_auth_methods')}
                      isRequired
                      multiple
                      clearable={false}
                      getValue={(item) => item}
                      getLabel={(item: AllowedAuthMethod) => authMethodsOptions[item]}
                      fixedOptions={(defaultCompany?.authMethods ?? []).filter((m) =>
                        FIXED_AUTH_METHODS.includes(m),
                      )}
                      inputProps={{
                        sx: {
                          '& .MuiInputBase-root': {
                            flexFlow: 'row wrap',
                          },
                        },
                      }}
                    />
                  ) : (
                    <ReadOnlyField
                      label={t('company.form.available_auth_methods')}
                      value={
                        watch('authMethods')
                          ?.map((method: AuthMethod) =>
                            method in authMethodsOptions
                              ? authMethodsOptions[method as AllowedAuthMethod]
                              : method,
                          )
                          .join(', ') || ''
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </Card>
          )}
        </>
      )}
      <Box
        sx={{
          flexDirection: 'column',
          mb: 3,
          display: 'none',
        }}
      >
        <Typography variant={'h6'}>{t('company.form.region_settings')}</Typography>
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: '50%',
                mr: 2,
              }}
            >
              <AutocompleteFormField
                clearable={false}
                control={control}
                disabled={!editable}
                errors={errors}
                getLabel={(item) => item}
                getValue={(item) => item}
                isRequired
                label={t('form_fields.time_zone')}
                list={enums.time_zone.values}
                name="timeZone"
              />
            </Box>

            <Box
              sx={{
                width: '50%',
              }}
            >
              <AutocompleteFormField
                clearable={false}
                control={control}
                disabled={!editable}
                errors={errors}
                getLabel={(item) => item}
                getValue={(item) => item}
                isRequired
                label={t('form_fields.date_format')}
                list={enums.date_format.values}
                name="dateFormat"
              />
            </Box>
          </Box>
        </Box>

        <Box>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Box
              sx={{
                width: '50%',
                mr: 2,
              }}
            >
              <RadioGroupFormField
                control={control}
                errors={errors}
                disabled={!editable}
                name="timeFormat"
                label={t('form_fields.time_format')}
                list={enums.time_format.values}
              />
            </Box>
            <Box
              sx={{
                width: '50%',
                mt: 2,
              }}
            >
              <AutocompleteFormField
                clearable={false}
                control={control}
                disabled={!editable}
                errors={errors}
                getLabel={(item) => item.toUpperCase()}
                getValue={(item) => item}
                isRequired
                label={t('form_fields.currency')}
                list={enums.currency.values}
                name="currency"
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flexDirection: 'column',
          display: 'none',
        }}
      >
        <Typography variant={'h6'}>{t('company.form.unit_settings')}</Typography>
        <Box
          sx={{
            display: 'flex',
            mt: 2,
          }}
        >
          <Box
            sx={{
              width: '50%',
              mr: 2,
            }}
          >
            <RadioGroupFormField
              control={control}
              errors={errors}
              disabled={!editable}
              name="unitOfDistance"
              label={t('form_fields.distance')}
              list={enums.unit_of_distance.values}
            />
          </Box>
          <Box
            sx={{
              width: '50%',
            }}
          >
            <RadioGroupFormField
              control={control}
              errors={errors}
              disabled={!editable}
              name="systemOfMeasure"
              label={t('form_fields.system_of_measure')}
              list={enums.system_of_measure.values}
            />
          </Box>
        </Box>
      </Box>
      {/*Submit buttons are moved to the parent component*/}
    </Box>
  );
});
export { CompanyForm };
